/**
 * VRG-471 — the one definition of "an address we are willing to send to".
 *
 * WHY THIS EXISTS. A certificate was transferred to `spencer@usevergence.cin`
 * (`.cin`, not `.com`). Everything reported success: the transfer was created, an
 * owner row was provisioned, two emails were sent, SendGrid never delivered
 * either, and the sender saw no error and no bounce. `@IsEmail()` on the server
 * and `EMAIL_RE` on the client are SYNTAX checks, and `spencer@usevergence.cin`
 * is syntactically perfect. Nothing anywhere asked whether `.cin` is a real
 * top-level domain. It is not, and no address under it can ever receive mail.
 *
 * WHAT THIS DOES AND DELIBERATELY DOES NOT DO. It answers one question offline
 * and deterministically: does the address end in a TLD that IANA has actually
 * delegated? That is a closed set of ~1.4k labels (`tld-list.generated.ts`), so
 * there is no network call, no latency, no resolver to leak the address to, and
 * no fail-open ambiguity — the three properties that make an MX lookup a
 * materially harder call. It does NOT claim the mailbox exists, that the domain
 * has an MX record, or that the address is not a plausible typo of another real
 * address. Those are different problems: the second is the bounce webhook's job
 * (`sendgrid-event.controller.ts`) and the third is `suggest-email-correction.ts`.
 *
 * WHY THE CLIENT COPY IS LOOSER THAN THE SERVER'S. `EMAIL_SYNTAX_REGEX` below is
 * intentionally more permissive than the server's `@IsEmail()`. A client stricter
 * than the server rejects addresses the product would have accepted, which is a
 * silent loss; a client looser than the server just defers to a 400 it was always
 * going to get. Given the choice, be looser. The TLD half is identical on both
 * sides because it reads the same generated table.
 */

import {IANA_TLDS} from './tld-list.generated.js';

/**
 * RFC 5321 caps a forward path at 256 octets including the angle brackets, so
 * 254 is the longest address that can legally traverse SMTP.
 */
export const MAX_EMAIL_LENGTH = 254;

/**
 * Anchored, requires exactly one `@`, requires at least one dot in the domain,
 * and forbids empty labels (so `a@b..com` and `a@.com` fail). Everything finer
 * than that — quoted local parts, comments, address literals — is left to the
 * server's `@IsEmail()`, per the looser-client rule above.
 */
export const EMAIL_SYNTAX_REGEX = /^[^\s@]+@[^\s@.]+(?:\.[^\s@.]+)+$/;

export type EmailRejectionCode =
  | 'empty'
  | 'too_long'
  | 'syntax'
  | 'unknown_tld';

export interface EmailValidationResult {
  ok: boolean;
  /** Trimmed and lowercased. `null` whenever `ok` is false. */
  normalized: string | null;
  code?: EmailRejectionCode;
  /**
   * User-facing, and phrased to be actionable. Notably `unknown_tld` names the
   * offending ending and points at support rather than saying "invalid email",
   * because the one way this check is wrong is a TLD delegated after the
   * snapshot date — in which case the address IS fine and the user needs a route
   * that is not "keep retyping it".
   */
  message?: string;
}

/** `foo@bar.co.uk` -> `{local: 'foo', domain: 'bar.co.uk'}`. */
export function splitEmailAddress(
  raw: string,
): {local: string; domain: string} | null {
  const trimmed = (raw ?? '').trim().toLowerCase();
  const at = trimmed.lastIndexOf('@');

  if (at <= 0 || at === trimmed.length - 1) {
    return null;
  }

  return {local: trimmed.slice(0, at), domain: trimmed.slice(at + 1)};
}

/** The label after the final dot. `bar.co.uk` -> `uk`. */
export function tldOf(domain: string): string {
  return domain.slice(domain.lastIndexOf('.') + 1);
}

/**
 * Is this label a delegated top-level domain?
 *
 * Any label containing a non-ASCII character is accepted OUTRIGHT rather than
 * looked up. IANA publishes internationalised TLDs only in their punycode
 * A-label form (`xn--p1ai`), so a U-label typed by a user (`рф`) would miss the
 * table and be rejected as fake. Converting between the two needs a punycode
 * implementation, and the population of IDN recipients here rounds to zero, so
 * the deliberate trade is: never block an IDN address, and accept that we do not
 * catch typos inside one.
 */
export function isKnownTld(label: string): boolean {
  const tld = label.trim().toLowerCase();

  if (!tld) {
    return false;
  }

  for (const character of tld) {
    if ((character.codePointAt(0) ?? 0) > 0x7f) {
      return true;
    }
  }

  return IANA_TLDS.has(tld);
}

/**
 * The narrow check the server enforces: syntax is somebody else's decorator, but
 * an address whose TLD does not exist is never deliverable. Anything that does
 * not parse as an address at all returns `true` here so this composes with (and
 * never duplicates the error of) whatever syntax validator runs alongside it.
 */
export function hasKnownTld(raw: string): boolean {
  const parts = splitEmailAddress(raw);

  if (!parts) {
    return true;
  }

  return isKnownTld(tldOf(parts.domain));
}

export function validateEmailAddress(raw: string): EmailValidationResult {
  const trimmed = (raw ?? '').trim().toLowerCase();

  if (!trimmed) {
    return {ok: false, normalized: null, code: 'empty', message: 'Enter an email address.'};
  }

  if (trimmed.length > MAX_EMAIL_LENGTH) {
    return {
      ok: false,
      normalized: null,
      code: 'too_long',
      message: 'That email address is too long.',
    };
  }

  if (!EMAIL_SYNTAX_REGEX.test(trimmed)) {
    return {
      ok: false,
      normalized: null,
      code: 'syntax',
      message: "That doesn't look like an email address.",
    };
  }

  const parts = splitEmailAddress(trimmed);

  if (!parts) {
    return {
      ok: false,
      normalized: null,
      code: 'syntax',
      message: "That doesn't look like an email address.",
    };
  }

  const tld = tldOf(parts.domain);

  if (!isKnownTld(tld)) {
    return {
      ok: false,
      normalized: null,
      code: 'unknown_tld',
      // No em-dash: this string is UI copy (it renders in the platform's transfer
      // picker and the app's gift picker), and working-conventions §15 asks for a
      // comma or a period over an em-dash-joined clause.
      message: `".${tld}" isn't a real domain ending, so mail sent there can't arrive. Check the spelling, or contact support if the address is correct.`,
    };
  }

  return {ok: true, normalized: trimmed};
}
